import React from 'react';

// ─── Shared vehicle field options ─────────────────────────────────────────────

export const VEHICLE_TYPE_OPTIONS = [
  { value: 'auto', label: 'Automóvil' },
  { value: 'suv', label: 'SUV / Camioneta' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'van', label: 'Van / Minivan' },
  { value: 'motorcycle', label: 'Motocicleta' },
  { value: 'truck', label: 'Camión' },
  { value: 'otro', label: 'Otro' },
];

export const FUEL_TYPE_OPTIONS = [
  { value: 'gasolina', label: 'Gasolina' },
  { value: 'diesel', label: 'Diésel' },
  { value: 'electrico', label: 'Eléctrico' },
  { value: 'hibrido', label: 'Híbrido' },
];

export const COLOR_OPTIONS = [
  { value: 'blanco', label: 'Blanco' },
  { value: 'negro', label: 'Negro' },
  { value: 'gris', label: 'Gris' },
  { value: 'plata', label: 'Plata' },
  { value: 'rojo', label: 'Rojo' },
  { value: 'azul', label: 'Azul' },
  { value: 'verde', label: 'Verde' },
  { value: 'amarillo', label: 'Amarillo' },
  { value: 'cafe', label: 'Café' },
  { value: 'otro', label: 'Otro' },
];

// ─── Required fields (same everywhere, VIN is NEVER required) ─────────────────

export const VEHICLE_REQUIRED_FIELDS = ['vehicleType', 'plates', 'brand', 'model', 'year', 'fuelType', 'odometer', 'color'];

// ─── Validate vehicle fields — VIN is always optional ─────────────────────────

export const validateVehicleFields = (formData) => {
  const errors = {};
  if (!formData?.vehicleType) errors.vehicleType = 'Seleccione el tipo de vehículo';
  // VIN is OPTIONAL — never validate as required
  if (formData?.vin?.trim() && formData?.vin?.trim()?.length !== 17) {
    errors.vin = 'El VIN debe tener exactamente 17 caracteres (o déjalo vacío)';
  }
  if (!formData?.plates?.trim()) errors.plates = 'Las placas son obligatorias';
  if (!formData?.brand?.trim()) errors.brand = 'La marca es obligatoria';
  if (!formData?.model?.trim()) errors.model = 'El modelo es obligatorio';
  if (!formData?.year) errors.year = 'El año es obligatorio';
  else if (Number(formData?.year) < 1900 || Number(formData?.year) > 2027) errors.year = 'Ingrese un año válido';
  if (!formData?.fuelType) errors.fuelType = 'Seleccione el tipo de combustible';
  if (!formData?.odometer && formData?.odometer !== 0) errors.odometer = 'El kilometraje es obligatorio';
  if (!formData?.color) errors.color = 'Seleccione el color';
  return errors;
};

// ─── Default empty vehicle form state ─────────────────────────────────────────

export const VEHICLE_FORM_DEFAULTS = {
  vehicleType: '',
  vin: '',
  plates: '',
  brand: '',
  model: '',
  year: '',
  engine: '',
  fuelType: '',
  odometer: '',
  color: '',
};

// ─── Inline field style helpers (for portal modals without Tailwind UI components) ─

const inputCls = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30';
const selectCls = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30';
const labelCls = 'block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5';
const errorCls = 'text-xs text-red-600 mt-1';

/**
 * VehicleFormUnified
 *
 * Renders all vehicle fields in a consistent 2-column grid.
 * Designed for portal modals (plain HTML inputs, no UI library).
 *
 * Props:
 *   formData   — { vehicleType, vin, plates, brand, model, year, engine, fuelType, odometer, color }
 *   onChange   — (field, value) => void
 *   errors     — { [field]: string }
 */
const VehicleFormUnified = ({ formData, onChange, errors }) => {
  const f = formData || {};
  const e = errors || {};

  return (
    <div className="space-y-3">
      {/* Row 1: Tipo + Placas */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Tipo de vehículo <span className="text-red-500">*</span></label>
          <select
            value={f?.vehicleType || ''}
            onChange={ev => onChange('vehicleType', ev?.target?.value)}
            className={selectCls}
          >
            <option value="">Seleccionar tipo</option>
            {VEHICLE_TYPE_OPTIONS?.map(o => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
          </select>
          {e?.vehicleType && <p className={errorCls}>{e?.vehicleType}</p>}
        </div>
        <div>
          <label className={labelCls}>Placas <span className="text-red-500">*</span></label>
          <input
            value={f?.plates || ''}
            onChange={ev => onChange('plates', ev?.target?.value?.toUpperCase())}
            placeholder="Ej. ABC-123-D"
            maxLength={10}
            className={inputCls}
          />
          {e?.plates && <p className={errorCls}>{e?.plates}</p>}
        </div>
      </div>

      {/* Row 2: Marca + Modelo */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Marca <span className="text-red-500">*</span></label>
          <input
            value={f?.brand || ''}
            onChange={ev => onChange('brand', ev?.target?.value)}
            placeholder="Ej. Toyota"
            className={inputCls}
          />
          {e?.brand && <p className={errorCls}>{e?.brand}</p>}
        </div>
        <div>
          <label className={labelCls}>Modelo <span className="text-red-500">*</span></label>
          <input
            value={f?.model || ''}
            onChange={ev => onChange('model', ev?.target?.value)}
            placeholder="Ej. Corolla"
            className={inputCls}
          />
          {e?.model && <p className={errorCls}>{e?.model}</p>}
        </div>
      </div>

      {/* Row 3: Año + Color */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Año <span className="text-red-500">*</span></label>
          <input
            type="number"
            value={f?.year || ''}
            onChange={ev => onChange('year', ev?.target?.value)}
            placeholder="Ej. 2020"
            min="1900"
            max="2027"
            className={inputCls}
          />
          {e?.year && <p className={errorCls}>{e?.year}</p>}
        </div>
        <div>
          <label className={labelCls}>Color <span className="text-red-500">*</span></label>
          <select
            value={f?.color || ''}
            onChange={ev => onChange('color', ev?.target?.value)}
            className={selectCls}
          >
            <option value="">Seleccionar color</option>
            {COLOR_OPTIONS?.map(o => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
          </select>
          {e?.color && <p className={errorCls}>{e?.color}</p>}
        </div>
      </div>

      {/* Row 4: Combustible + Kilometraje */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Tipo de combustible <span className="text-red-500">*</span></label>
          <select
            value={f?.fuelType || ''}
            onChange={ev => onChange('fuelType', ev?.target?.value)}
            className={selectCls}
          >
            <option value="">Seleccionar</option>
            {FUEL_TYPE_OPTIONS?.map(o => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
          </select>
          {e?.fuelType && <p className={errorCls}>{e?.fuelType}</p>}
        </div>
        <div>
          <label className={labelCls}>Kilometraje <span className="text-red-500">*</span></label>
          <input
            type="number"
            value={f?.odometer || ''}
            onChange={ev => onChange('odometer', ev?.target?.value)}
            placeholder="Ej. 45000"
            min="0"
            className={inputCls}
          />
          {e?.odometer && <p className={errorCls}>{e?.odometer}</p>}
        </div>
      </div>

      {/* Row 5: Motor (full width) */}
      <div>
        <label className={labelCls}>Motor</label>
        <input
          value={f?.engine || ''}
          onChange={ev => onChange('engine', ev?.target?.value)}
          placeholder="Ej. 2.0L Turbo, 1.5L"
          className={inputCls}
        />
      </div>

      {/* Row 6: VIN — always optional */}
      <div>
        <label className={labelCls}>VIN — Número de serie <span className="text-gray-400 font-normal normal-case">(opcional)</span></label>
        <input
          value={f?.vin || ''}
          onChange={ev => onChange('vin', ev?.target?.value?.toUpperCase())}
          placeholder="17 caracteres alfanuméricos (opcional)"
          maxLength={17}
          className={inputCls}
        />
        {e?.vin && <p className={errorCls}>{e?.vin}</p>}
      </div>
    </div>
  );
};

export default VehicleFormUnified;
